import {Suspense, useEffect, useRef, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApiEu} from '@di-shared/api/di';
import {ApiResponseSafetyScoreDailyResponse} from '@di-shared/api/di/__generated_eu';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {ERROR} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import ConsentBannerEu from '@di-shared/features/home/eu/ConsentBanner.eu';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import SafeDrivingDailyGraph from '@di-shared/features/safe-driving-vDSP/SafeDrivingDailyGraph';
import SafeDrivingMonthlyScoreTrend from '@di-shared/features/safe-driving-vDSP/SafeDrivingMonthlyScoreTrend';
import useSafeDrivingEu from '@di-shared/features/safe-driving-vDSP/useSafeDriving.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface SafeDrivingContentsEuProps {
  vin: string;
}

// 최대 90일간의 날짜 조회 => 90일치의 데이터가 보여야함 (오늘을 포함하기 때문)
export const MAX_GRAPH_LENGTH = 90;

export default function ({vin}: SafeDrivingContentsEuProps) {
  const {t} = useInferableTranslation();
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_title_eu')}</h1>
      </Header>
      <ErrorBoundary fallback={<SafeDrivingContentsError />}>
        <Suspense fallback={<SafeDrivingContentsLoading />}>
          <Inner vin={vin} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function Inner({vin}: SafeDrivingContentsEuProps) {
  const {t} = useInferableTranslation();
  const today = new Date();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {openAlert, backOrClose} = useCcWebviewUtilsHelper();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const {
    scoreDate,
    brstAccCount,
    safetyDrvScore,
    nightDrvCount,
    strtBrstAccCnt,
    brstDecCount,
    rangeDrvDist,
    fromAgreeDate,
    daysOfRecentlyTrips,
    predictionScoreFromDistances,
  } = useSafeDrivingEu(vin);
  const {data: dailySafeDrivingData} = useSuspenseQuery({
    queryKey: [
      getQueryKey(diApiEu.safeDriving.daily),
      vin,
      getYYYYMMddFormat(subDate(today, {days: MAX_GRAPH_LENGTH - 1})),
      getYYYYMMddFormat(today),
    ],
    queryFn: () =>
      diApiEu.safeDriving.daily({
        vin,
        startDate: getYYYYMMddFormat(subDate(today, {days: MAX_GRAPH_LENGTH - 1})),
        endDate: getYYYYMMddFormat(today),
      }),
    select: ({data}: ApiResponseSafetyScoreDailyResponse) => {
      return {
        ...data,
        ubi: data.dailyScoreList.map((ubiItem) => {
          return {
            ...ubiItem,
            scoreDate: parseDateWithFormat(ubiItem.scoreDateTime),
          };
        }),
      };
    },
  });

  const initialSelectedButtonRef = useRef<HTMLButtonElement>(null);
  const predictGraphBarRef = useRef<HTMLSpanElement>(null);
  const [isOpenScoreStandard, setIsOpenScoreStandard] = useState(true);
  const [predictIndex, setPredictIndex] = useState(0);
  const [predictBarWidth, setPredictBarWidth] = useState<string | number>(0);
  const [isDataAvailable, setIsDataAvailable] = useState(false);

  async function openNoDataAlert() {
    await openAlert({
      title: t('msg_dss_not_enough_data_eu'),
      message: t('msg_dss_no_recorded_drives_yet_eu'),
      okText: t('ctn_dss_ok_button_eu'),
    });
    backOrClose();
  }

  function changeSelectedScore(targetWidth: number, targetOffsetLeft: number, index: number) {
    if (predictGraphBarRef.current) {
      const graphBar = predictGraphBarRef.current;
      const barWidth = index === 3 ? '100%' : Math.round(targetWidth / 2 + targetOffsetLeft - graphBar.offsetLeft + 4);

      setPredictIndex(index);
      setPredictBarWidth(barWidth);
    }
  }

  useEffect(() => {
    // 0. data 구성 불가 case
    if (safetyDrvScore === 0 && rangeDrvDist === 0) {
      openNoDataAlert();
      return;
    }
    setIsDataAvailable(true);
  }, []);

  useEffect(() => {
    const target = initialSelectedButtonRef.current;
    if (target) {
      // DEFAULT : 500KM
      changeSelectedScore(target.offsetWidth, target.offsetLeft, 2);
    }
  }, [isDataAvailable]);

  if (!isDataAvailable) {
    return <></>;
  }

  return (
    <main className="content page_safe_driving">
      <ConsentBannerEu />
      <section className="section section_safe_driving">
        <div className="my_driving_score">
          <div className="my_score_box">
            <div className="my_score_imgbox">
              <LottieSelector
                id={'safe_driving_score_good_light'}
                kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/json/hyundai/safe_driving_score_good.json')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/json/hyundai/safe_driving_score_good.json')}
                genesisLightSrc={() => import('@di-shared/assets/img/json/genesis/safe_driving_score_good.json')}
                genesisDarkSrc={() => import('@di-shared/assets/img/json/genesis/safe_driving_score_good.json')}
                loop={true}
                autoplay={true}
              />
            </div>
            <div className="my_score_info">
              <div className="my_score_point">
                <span className="my_score_value">{safetyDrvScore}</span>
                <span className="my_score_unit">{t('ctn_dss_$score_points_eu')}</span>
              </div>
              {safetyDrvScore === 0 && rangeDrvDist === 0 ? (
                <span className="my_score_update">{ERROR.DASH}</span>
              ) : (
                <span className="my_score_update">
                  {t('ctn_dss_as_of_$date_eu', {date: scoreDate ? CldrDatetimeFormatter.format(scoreDate, 'yMMMaHM') : ERROR.DASH})}
                </span>
              )}
            </div>
          </div>
          <div className="score_factor">
            <ul className="score_factor_list">
              <li className="score_factor_item">
                <span className="score_factor_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
                <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: brstAccCount})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
                <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: brstDecCount})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('ctn_dss_sudden_start_eu')}</span>
                <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: strtBrstAccCnt})}</span>
              </li>
              <li className="score_factor_item">
                <span className="score_factor_title">{t('ctn_dss_night_driving_eu')}</span>
                <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: nightDrvCount})}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="section_container safe_driving_standard">
          <div className="driving_accordion type_container">
            <div className="driving_accordion_item">
              <h3>
                <button
                  type="button"
                  className="driving_accordion_btn"
                  aria-expanded={isOpenScoreStandard}
                  aria-controls="accordion_content01"
                  onClick={() => {
                    emitEventLog({
                      event: 'di_driveinfo_interaction',
                      data_action: 'hide banner',
                      data_name: t('msg_dss_criteria_for_driving_safety_score_eu'),
                    });
                    setIsOpenScoreStandard((prev) => !prev);
                  }}
                >
                  <span className="driving_accordion_title">{t('msg_dss_criteria_for_driving_safety_score_eu')}</span>
                  <span
                    className="ic ic_arrow_down_16px"
                    aria-hidden="true"
                  ></span>
                </button>
              </h3>
              {isOpenScoreStandard && (
                <div
                  className="driving_accordion_content"
                  id="accordion_content01"
                  style={{display: 'block'}}
                >
                  <ul className="score_standard_list">
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span className="ic ic_calendar_24px"></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('ctn_dss_calculation_period_eu')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('msg_dss_calculation_period_explain_$fromDate_$toDate_$totalDays_eu', {
                            fromDate: fromAgreeDate,
                            toDate: scoreDate ? CldrDatetimeFormatter.format(scoreDate, 'long') : ERROR.DASH,
                            totalDays: daysOfRecentlyTrips,
                          })}
                        </span>
                      </div>
                    </li>
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span
                          className="ic ic_drive_distance_24px"
                          aria-hidden="true"
                        ></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('ctn_dss_mileage_of_calculation_period_eu')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('msg_dss_mileage_of_calculation_period_explain_$distance_$days_eu', {
                            days: daysOfRecentlyTrips,
                            distance: `${convertDistanceValue(rangeDrvDist)}${convertDistanceSymbol(rangeDrvDist)}`,
                          })}
                        </span>
                      </div>
                    </li>
                  </ul>

                  <div className="driving_banner type_square">
                    <LinkTo
                      to={ROUTE_PATHS.SAFE_DRIVING_GUIDE.getFullPath()}
                      className="driving_banner_box"
                      onClick={() =>
                        emitEventLog({
                          event: 'di_driveinfo_interaction',
                          data_action: 'view banner',
                          data_name: t('msg_dss_check_driving_safety_score_guide_eu'),
                        })
                      }
                    >
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_medium_b">{t('msg_dss_check_driving_safety_score_guide_eu')}</strong>
                        <span className="driving_banner_desc label_small_b">{t('msg_dss_have_more_questions_eu')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                        />
                      </span>
                    </LinkTo>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="section_container safe_driving_daily">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('ctn_dss_daily_score_trend_eu')}</h3>
          </div>
          <ErrorBoundary
            fallback={
              <>
                <p className="section_container_desc">{t('msg_dss_generating_score')}</p>
                <DailyLineGraphError />
              </>
            }
          >
            <SafeDrivingDailyGraph
              data={[
                ...dailySafeDrivingData.ubi.map((item) => ({
                  score: item.safetyDrvScore,
                  scoreDate: item.scoreDate,
                })),
              ].slice(-MAX_GRAPH_LENGTH)}
            />
          </ErrorBoundary>

          <ul className="statistics_info_list">
            <li className="statistics_info_item">
              <span className="statistics_info_key">{t('ctn_dss_lowest_point_eu')}</span>
              <span className="statistics_info_val">
                <span className="statistics_info_number">{dailySafeDrivingData.minScore}</span>
                <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
              </span>
            </li>
            <li className="statistics_info_item">
              <span className="statistics_info_key">{t('ctn_dss_highest_point_eu')}</span>
              <span className="statistics_info_val">
                <span className="statistics_info_number">{dailySafeDrivingData.maxScore}</span>
                <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
              </span>
            </li>
          </ul>
        </div>
        <SafeDrivingMonthlyScoreTrend vin={vin} />

        <div className="section_container safe_driving_predict">
          <div className="section_container_header">
            <h3 className="title_xxsmall">{t('ctn_dss_estimated_driving_safety_score_eu')}</h3>
          </div>
          <p
            className="section_container_desc"
            id="scorePredictDescription"
            role="tabpanel"
            aria-labelledby="scorePredict500"
            aria-live="polite"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('msg_dss_you_can_get_n_points_$distance_$score_eu', {
              distance: `${convertDistanceValue(predictionScoreFromDistances[predictIndex].distance)}${convertDistanceSymbol(
                predictionScoreFromDistances[predictIndex].distance
              )}`,
              score: predictionScoreFromDistances[predictIndex].score,
            })}
          </p>
          <div className="score_predict">
            <span className="speech_bubble">
              <span className="score_predict_point">{t('ctn_dss_points_$score_eu', {score: safetyDrvScore})}</span>
              <span className="score_predict_distance">
                {convertDistanceValue(rangeDrvDist)}
                {convertDistanceSymbol(rangeDrvDist)}
              </span>
            </span>
            <span
              className="predict_graph_bar"
              ref={predictGraphBarRef}
            >
              <span
                className={clsx('bar_current', {
                  'is-end': predictIndex === predictionScoreFromDistances.length - 1,
                })}
                style={{width: predictBarWidth}}
              >
                <span
                  className="speech_bubble"
                  aria-hidden="true"
                >
                  <span className="score_predict_point">
                    {t('ctn_dss_points_$score_eu', {score: predictionScoreFromDistances[predictIndex].score})}
                  </span>
                  <span className="score_predict_distance">
                    {convertDistanceValue(rangeDrvDist + predictionScoreFromDistances[predictIndex].distance)}
                    {convertDistanceSymbol(rangeDrvDist + predictionScoreFromDistances[predictIndex].distance)}
                  </span>
                </span>
              </span>
            </span>
            <div
              className="score_predict_control"
              role="tablist"
            >
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredictCurrent"
                role="tab"
                aria-selected="false"
                aria-controls="scorePredictDescription"
                disabled={true}
              >
                {t('ctn_dss_current')}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict100"
                role="tab"
                aria-selected={predictIndex === 0}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 0);
                }}
              >
                +{convertDistanceValue(100)}
                {convertDistanceSymbol(100)}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict300"
                role="tab"
                aria-selected={predictIndex === 1}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 1);
                }}
              >
                +{convertDistanceValue(300)}
                {convertDistanceSymbol(300)}
              </button>
              <button
                ref={initialSelectedButtonRef}
                type="button"
                className="score_predict_btn"
                id="scorePredict500"
                role="tab"
                aria-selected={predictIndex === 2}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 2);
                }}
              >
                +{convertDistanceValue(500)}
                {convertDistanceSymbol(500)}
              </button>
              <button
                type="button"
                className="score_predict_btn"
                id="scorePredict1000"
                role="tab"
                aria-selected={predictIndex === 3}
                aria-controls="scorePredictDescription"
                onClick={(e) => {
                  const target = e.target as HTMLButtonElement;
                  changeSelectedScore(target.offsetWidth, target.offsetLeft, 3);
                }}
              >
                +{convertDistanceValue(1000)}
                {convertDistanceSymbol(1000)}
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function SafeDrivingContentsError() {
  enum TripTab {
    SAFE_DRIVING,
    RECKLESS_DRIVING,
  }

  const {t} = useInferableTranslation();
  const [selectedTripTab, setSelectedTripTab] = useState<TripTab>(TripTab.RECKLESS_DRIVING);
  const initialSelectedButtonRef = useRef<HTMLButtonElement>(null);
  const predictGraphBarRef = useRef<HTMLSpanElement>(null);
  const [isOpenScoreStandard, setIsOpenScoreStandard] = useState(false);
  const [predictIndex, setPredictIndex] = useState(0);
  const [predictBarWidth, setPredictBarWidth] = useState<string | number>(0);
  const {convertDistanceValue, convertDistanceSymbol, defaultDistanceSymbol} = useUnitConverter();

  function changeSelectedScore(targetWidth: number, targetOffsetLeft: number, index: number) {
    if (predictGraphBarRef.current) {
      const graphBar = predictGraphBarRef.current;
      const barWidth = index === 3 ? '100%' : Math.round(targetWidth / 2 + targetOffsetLeft - graphBar.offsetLeft + 4);

      setPredictIndex(index);
      setPredictBarWidth(barWidth);
    }
  }

  useEffect(() => {
    const target = initialSelectedButtonRef.current;
    if (target) {
      changeSelectedScore(target.offsetWidth, target.offsetLeft, 0);
    }
  }, []);

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_title_eu')}</h1>
      </Header>
      <main className="content page_safe_driving">
        <section className="section section_safe_driving">
          <div className="my_driving_score">
            <div className="my_score_box">
              <div className="my_score_imgbox">
                <LottieSelector
                  id={'safe_driving_score_good_light'}
                  kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  loop={true}
                  autoplay={true}
                />
              </div>
              <div className="my_score_info">
                <div className="my_score_point">
                  <span className="my_score_value">{ERROR.DASH}</span>
                  <span className="my_score_unit">{t('ctn_dss_$score_points_eu')}</span>
                </div>
                <span className="my_score_update">{t('ctn_dss_as_of_$date_eu', {date: ERROR.DASH})}</span>
              </div>
            </div>
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_start_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_night_driving_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: ERROR.DASH})}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_standard">
            <div className="driving_accordion type_container">
              <div className="driving_accordion_item">
                <h3>
                  <button
                    type="button"
                    className="driving_accordion_btn"
                    aria-expanded={isOpenScoreStandard}
                    aria-controls="accordion_content01"
                    onClick={() => {
                      emitEventLog({
                        event: 'di_driveinfo_interaction',
                        data_action: 'hide banner',
                        data_name: t('msg_dss_criteria_for_driving_safety_score_eu'),
                      });
                      setIsOpenScoreStandard((prev) => !prev);
                    }}
                  >
                    <span className="driving_accordion_title">{t('msg_dss_criteria_for_driving_safety_score_eu')}</span>
                    <span
                      className="ic ic_arrow_down_16px"
                      aria-hidden="true"
                    ></span>
                  </button>
                </h3>
                {isOpenScoreStandard && (
                  <div
                    className="driving_accordion_content"
                    id="accordion_content01"
                    style={{display: 'block'}}
                  >
                    <ul className="score_standard_list">
                      <li className="score_standard_item">
                        <div className="score_standard_icon">
                          <span className="ic ic_calendar_24px"></span>
                        </div>
                        <div className="score_standard_info">
                          <strong className="score_standard_title">{t('ctn_dss_calculation_period_eu')}</strong>
                          <span
                            className="score_standard_desc"
                            style={{whiteSpace: 'pre-wrap'}}
                          >
                            {t('msg_dss_calculation_period_explain_$fromDate_$toDate_$totalDays_eu', {
                              fromDate: ERROR.DASH,
                              toDate: ERROR.DASH,
                              totalDays: ERROR.DASH,
                            })}
                          </span>
                        </div>
                      </li>
                      <li className="score_standard_item">
                        <div className="score_standard_icon">
                          <span
                            className="ic ic_drive_distance_24px"
                            aria-hidden="true"
                          ></span>
                        </div>
                        <div className="score_standard_info">
                          <strong className="score_standard_title">{t('ctn_dss_mileage_of_calculation_period_eu')}</strong>
                          <span
                            className="score_standard_desc"
                            style={{whiteSpace: 'pre-wrap'}}
                          >
                            {t('msg_dss_mileage_of_calculation_period_explain_$distance_$days_eu', {
                              days: ERROR.DASH,
                              distance: ERROR.DASH,
                            })}
                          </span>
                        </div>
                      </li>
                    </ul>

                    <div className="driving_banner type_square">
                      <LinkTo
                        to={ROUTE_PATHS.SAFE_DRIVING_GUIDE.getFullPath()}
                        className="driving_banner_box"
                        onClick={() =>
                          emitEventLog({
                            event: 'di_driveinfo_interaction',
                            data_action: 'view banner',
                            data_name: t('msg_dss_check_driving_safety_score_guide_eu'),
                          })
                        }
                      >
                        <span className="driving_banner_txtbox type_reverse">
                          <strong className="driving_banner_title body_medium_b">{t('msg_dss_check_driving_safety_score_guide_eu')}</strong>
                          <span className="driving_banner_desc label_small_b">{t('msg_dss_have_more_questions_eu')}</span>
                        </span>
                        <span className="driving_banner_imgbox">
                          <ImageSelector
                            className="driving_banner_img"
                            alt="driving_banner_img"
                            kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                            kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                            hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                            hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                            genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                            genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          />
                        </span>
                      </LinkTo>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="section_container safe_driving_daily">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_daily_score_trend_eu')}</h3>
            </div>
            <DailyLineGraphError description={t('msg_dss_generating_score')} />

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('ctn_dss_lowest_point_eu')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('ctn_dss_highest_point_eu')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{ERROR.DASH}</span>
                  <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_predict">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_estimated_driving_safety_score_eu')}</h3>
            </div>
            <p
              className="section_container_desc"
              id="scorePredictDescription"
              role="tabpanel"
              aria-labelledby="scorePredict500"
              aria-live="polite"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('msg_dss_you_can_get_n_points_$distance_$score_eu', {
                distance: `${ERROR.DASH}${defaultDistanceSymbol}`,
                score: ERROR.DASH,
              })}
            </p>
            <div className="score_predict">
              <span className="speech_bubble">
                <span className="score_predict_point">{t('ctn_dss_points_$score_eu', {score: ERROR.DASH})}</span>
                <span className="score_predict_distance">
                  {ERROR.DASH}
                  {defaultDistanceSymbol}
                </span>
              </span>
              <span
                className="predict_graph_bar"
                ref={predictGraphBarRef}
              >
                <span
                  className={clsx('bar_current')}
                  style={{width: predictBarWidth}}
                >
                  <span
                    className="speech_bubble"
                    aria-hidden="true"
                  >
                    <span className="score_predict_point">{t('ctn_dss_points_$score_eu', {score: ERROR.DASH})}</span>
                    <span className="score_predict_distance">
                      {ERROR.DASH}
                      {defaultDistanceSymbol}
                    </span>
                  </span>
                </span>
              </span>
              <div
                className="score_predict_control"
                role="tablist"
              >
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredictCurrent"
                  role="tab"
                  aria-selected="false"
                  aria-controls="scorePredictDescription"
                  disabled={true}
                >
                  {t('ctn_dss_current')}
                </button>
                <button
                  ref={initialSelectedButtonRef}
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict100"
                  role="tab"
                  aria-selected={predictIndex === 0}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(100)}
                  {convertDistanceSymbol(100)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict300"
                  role="tab"
                  aria-selected={predictIndex === 1}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(300)}
                  {convertDistanceSymbol(300)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict500"
                  role="tab"
                  aria-selected={predictIndex === 2}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(500)}
                  {convertDistanceSymbol(500)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict1000"
                  role="tab"
                  aria-selected={predictIndex === 3}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(1000)}
                  {convertDistanceSymbol(1000)}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function SafeDrivingContentsLoading() {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('ctn_dss_title_eu')}</h1>
      </Header>
      <main className="content page_safe_driving">
        <section className="section section_safe_driving">
          <div className="my_driving_score skeleton">
            <div className="my_score_box">
              <div className="my_score_imgbox skeleton">
                <LottieSelector
                  id={'safe_driving_score_good_light'}
                  kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  loop={true}
                  autoplay={true}
                />
              </div>
              <div className="my_score_info">
                <div className="my_score_point">
                  <span className="my_score_value">{54}</span>
                  <span className="my_score_unit">{t('ctn_dss_$score_points_eu')}</span>
                </div>
                <span className="my_score_update">
                  {t('ctn_dss_as_of_$date_eu', {date: DashedDatetimeFormatter.format(new Date(), 'datetime')})}
                </span>
              </div>
            </div>
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_acceleration_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: 3})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_deceleration_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: 4})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_sudden_start_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: 5})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('ctn_dss_night_driving_eu')}</span>
                  <span className="score_factor_value">{t('ctn_dss_n_times_$n_eu', {n: 6})}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_standard skeleton">
            <div className="driving_accordion type_container">
              <div className="driving_accordion_item">
                <h3>
                  <button
                    type="button"
                    className="driving_accordion_btn"
                    aria-expanded={true}
                    aria-controls="accordion_content01"
                  >
                    <span className="driving_accordion_title">{t('msg_dss_criteria_for_driving_safety_score_eu')}</span>
                    <span
                      className="ic ic_arrow_down_16px"
                      aria-hidden="true"
                    ></span>
                  </button>
                </h3>
                <div
                  className="driving_accordion_content"
                  id="accordion_content01"
                  style={{display: 'block'}}
                >
                  <ul className="score_standard_list">
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span className="ic ic_calendar_24px"></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('ctn_dss_calculation_period_eu')}</strong>
                        <span className="score_standard_desc">
                          {t('msg_dss_calculation_period_explain_$fromDate_$toDate_$totalDays_eu', {
                            fromDate: '2024-05-10',
                            toDate: DashedDatetimeFormatter.format(new Date(), 'date'),
                            totalDays: '2024-05-11',
                          })}
                        </span>
                      </div>
                    </li>
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span
                          className="ic ic_drive_distance_24px"
                          aria-hidden="true"
                        ></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('ctn_dss_mileage_of_calculation_period_eu')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('msg_dss_mileage_of_calculation_period_explain_$distance_$days_eu', {
                            days: 4,
                            distance: `${convertDistanceValue(1000)}${convertDistanceSymbol(1000)}`,
                          })}
                        </span>
                      </div>
                    </li>
                  </ul>

                  <div className="driving_banner type_square">
                    <a
                      role="button"
                      className="driving_banner_box"
                    >
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_medium_b">{t('msg_dss_check_driving_safety_score_guide_eu')}</strong>
                        <span className="driving_banner_desc label_small_b">{t('msg_dss_have_more_questions_eu')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                        />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section_container safe_driving_daily skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_daily_score_trend_eu')}</h3>
            </div>
            {/*<SafeDrivingDailyScoreGraph*/}
            {/*  // 항상 90일치의 그래프가 보이도록*/}
            {/*  data={[...new Array(MAX_GRAPH_LENGTH - 0).fill(null).ubi]}*/}
            {/*  fromDate={subDate(new Date(), {days: MAX_GRAPH_LENGTH - 1})}*/}
            {/*/>*/}

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('ctn_dss_lowest_point_eu')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{50}</span>
                  <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('ctn_dss_highest_point_eu')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{60}</span>
                  <span className="statistics_info_unit">{t('ctn_dss_$score_points_eu')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_predict skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('ctn_dss_estimated_driving_safety_score_eu')}</h3>
            </div>
            <p
              className="section_container_desc"
              id="scorePredictDescription"
              role="tabpanel"
              aria-labelledby="scorePredict500"
              aria-live="polite"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('msg_dss_you_can_get_n_points_$distance_$score_eu', {
                distance: `300`,
                score: 55,
              })}
            </p>
            <div className="score_predict">
              <span className="speech_bubble">
                <span className="score_predict_point">{t('ctn_dss_points_$score_eu', {score: 55})}</span>
                <span className="score_predict_distance">
                  {convertDistanceValue(2900)}
                  {convertDistanceSymbol(2900)}
                </span>
              </span>
              <span className="predict_graph_bar">
                <span className={clsx('bar_current')}>
                  <span
                    className="speech_bubble"
                    aria-hidden="true"
                  >
                    <span className="score_predict_point">{t('ctn_dss_points_$score_eu', {score: 55})}</span>
                    <span className="score_predict_distance">
                      {convertDistanceValue(1000)}
                      {convertDistanceSymbol(1000)}
                    </span>
                  </span>
                </span>
              </span>
              <div
                className="score_predict_control"
                role="tablist"
              >
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredictCurrent"
                  role="tab"
                  aria-selected="false"
                  aria-controls="scorePredictDescription"
                  disabled={true}
                >
                  {t('ctn_dss_current')}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict100"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(100)}
                  {convertDistanceSymbol(100)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict300"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(300)}
                  {convertDistanceSymbol(300)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict500"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(500)}
                  {convertDistanceSymbol(500)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict1000"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(1000)}
                  {convertDistanceSymbol(1000)}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
